import React, { FC, ReactElement, useContext, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useErrorController } from "@natera/platform/lib/hooks";
import { Button } from "@natera/form";
import { sendTokenizedOrOrganicHeapEvent, useQuery } from "@app/utils";
import { IDP_TYPE, EnrollFactorResponse } from "@app/service/user";
import {
  ConfigContext,
  ErrorProvider,
  getFilteredFactors,
  LoginMode,
  MfaErrorProvider,
  NotificationContext,
  ServiceContext,
  UserContext,
  useSignInContext,
} from "@app/provider";
import { LoginStatus } from "@app/provider/user";
import { HEAP_EVENTS } from "@app/provider/types";
import { CredentialsIdentityProvider } from "@natera/platform/lib/service/session/session";

import {
  AuthTitle,
  ChooseSignInTwoFactorMethod,
  LoginModeSwitch,
  Separator,
  NeedHelpLink,
  ForgotPasswordLink,
} from "@app/components";
import { AuthenticationButton } from "@app/components/signInWithAuthenticationProviders";
import VerifyModal from "@app/components/verifyModal";
import { LoginForm } from "@app/components/forms";
import SpinnerView from "@app/components/spinnerView";

import { usePortalRedirects, useTokenSourceFromQuery } from "@app/hooks";
import { OneTimeCodeForm } from "../signIn/oneTimeCode";

import "./auth.scss";
import "@app/components/auth/authTitle/authTitle.scss";
import "../signIn/signIn.scss";

const messages = defineMessages({
  logInRedirectionGoogle: {
    id: "logInRedirectionGoogle",
    defaultMessage:
      "It looks like you signed up with Google. To access your account, please log in with Google or use your email and one time code.",
  },
  logInRedirectionApple: {
    id: "logInRedirectionApple",
    defaultMessage:
      "It looks like you signed up with Apple. To access your account, please log in with Apple or use your email and one time code.",
  },
  logInRedirectionEmail: {
    id: "logInRedirectionEmail",
    defaultMessage:
      "Our records show that you created an account using an email and password. Please log in using one time code or your email and password.",
  },
  logInRedirectionBackButton: {
    id: "patientAcknowledgmentFooterBackButton",
    defaultMessage: "Back",
  },
});

const BackButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  const intl = useIntl();
  return (
    <Button
      className="log-in-redirection__back-btn"
      outlined
      data-testid="cancel"
      onClick={onClick}
    >
      {intl.formatMessage(messages.logInRedirectionBackButton)}
    </Button>
  );
};

const LoginRedirect: FC = () => {
  const intl = useIntl();
  const query = useQuery();
  const { token, tokenSource } = useTokenSourceFromQuery();
  const { toBack } = usePortalRedirects();
  const idp = query.get("provider");
  const prefilledEmailFromQuery = query.get("email")?.trim() || undefined;

  const { loadProfile, logUserLogin } = useContext(UserContext);
  const { clear } = useContext(NotificationContext);
  const { sessionService } = useContext(ServiceContext);
  const errorController = useErrorController();
  const {
    loginMode,
    switchLoginMode,
    handleOtpRequest,
    isOtpRequestPending,
    otpErrorMessage,
    handlePasswordSubmit,
    factors,
    setFactors,
    factor,
    setFactor,
    showFactorSelection,
    setShowFactorSelection,
    isLoginPending,
  } = useSignInContext();
  const { config } = useContext(ConfigContext);

  const showMfaFactorSelection = Boolean(
    factors?.length && showFactorSelection
  );
  const showVerifyStep = Boolean(factor);
  const showMfaOrVerifyStep = showMfaFactorSelection || showVerifyStep;

  const isOtpVerificationEnabled = config.otpVerification?.enabled;

  const oneTimeCodeFormConfig = useMemo(
    () => ({
      onSubmit: handleOtpRequest,
      isLoading: isOtpRequestPending,
      errorMessage: otpErrorMessage,
      prefilledEmail: prefilledEmailFromQuery,
      usePlusSignInEmailAddress: true,
      contactUsUrl: "",
    }),
    [
      handleOtpRequest,
      isOtpRequestPending,
      otpErrorMessage,
      prefilledEmailFromQuery,
    ]
  );

  const pageTitle: ReactElement = useMemo(() => {
    const subtitle =
      idp === IDP_TYPE.GOOGLE
        ? intl.formatMessage(messages.logInRedirectionGoogle)
        : idp === IDP_TYPE.APPLE
          ? intl.formatMessage(messages.logInRedirectionApple)
          : intl.formatMessage(messages.logInRedirectionEmail);
    return <AuthTitle isRedirectPage subtitle={subtitle} />;
  }, [idp, intl]);

  const handleIdpAuth = async (
    credentials: CredentialsIdentityProvider,
    provider: IDP_TYPE
  ) => {
    clear();
    try {
      if (await sessionService.getToken()) {
        await sessionService.clearToken();
      }
      const result = (await sessionService.loginIdentityProvider(
        credentials
      )) as { transaction?: { factors?: EnrollFactorResponse[] } } | void;
      const idpFactors = result?.transaction?.factors;
      const filteredFactors = idpFactors?.length
        ? getFilteredFactors(idpFactors)
        : undefined;

      if (filteredFactors?.length) {
        setFactors(filteredFactors);
        if (filteredFactors.length === 1) {
          setFactor(filteredFactors[0]);
        } else {
          setShowFactorSelection(true);
        }
        return;
      }

      const uppUser = await loadProfile();
      if (uppUser) {
        await logUserLogin({
          email: uppUser.email,
          status: LoginStatus.SUCCESS,
          token,
          idpType: uppUser.idpType,
        });
        await sendTokenizedOrOrganicHeapEvent({
          invite: token,
          tokenizedEventName: HEAP_EVENTS.upp_tokenizedlogin_success,
          organicEventName: HEAP_EVENTS.upp_organiclogin_success,
          method: provider,
          source: tokenSource,
        });
      }
    } catch (error) {
      await sendTokenizedOrOrganicHeapEvent({
        invite: token,
        tokenizedEventName: HEAP_EVENTS.upp_tokenizedlogin_failure,
        organicEventName: HEAP_EVENTS.upp_organiclogin_failure,
        method: provider,
        source: tokenSource,
      });
      console.error(error);
    }
  };

  const handleMfaSelectFactor = (selectedFactor?: typeof factor) => {
    setFactor(selectedFactor);
    setShowFactorSelection(false);
  };

  // "Go back" button on the method picker step: leaves MFA and shows the redirect login block again.
  const handleMfaFactorSelectionGoBack = () => {
    setFactor(undefined);
    setShowFactorSelection(false);
  };

  // "Back" button on the code entry step returns user to the method picker if he had multiple factors
  const handleMfaVerifyGoBack = () => {
    setFactor(undefined);
    setShowFactorSelection((factors?.length ?? 0) > 1);
  };

  /** 2FA blocks: choice of method + verify code (for Email, Google, Apple) */
  const renderMfaBlock = () => (
    <div className="log-in-redirection__idp-email-block">
      <MfaErrorProvider>
        <article className="sign-in__container">
          {showMfaFactorSelection && (
            <ChooseSignInTwoFactorMethod
              factors={factors!}
              onSubmitFactor={handleMfaSelectFactor}
              goBack={handleMfaFactorSelectionGoBack}
            />
          )}
          {showVerifyStep && !showMfaFactorSelection && (
            <VerifyModal
              factor={factor!}
              setFactor={setFactor}
              goBack={handleMfaVerifyGoBack}
            />
          )}
        </article>
      </MfaErrorProvider>
    </div>
  );

  const renderIdpBlock = (provider: IDP_TYPE) => (
    <div className="log-in-redirection__authentication-btn">
      <AuthenticationButton onClick={handleIdpAuth} idp={provider} />
      {isOtpVerificationEnabled && (
        <>
          <Separator />
          <ErrorProvider controller={errorController}>
            <OneTimeCodeForm {...oneTimeCodeFormConfig} />
          </ErrorProvider>
        </>
      )}
      <BackButton onClick={toBack} />
      <NeedHelpLink />
    </div>
  );

  const renderEmailBlock = () => (
    <div className="log-in-redirection__idp-email-block">
      <MfaErrorProvider>
        {!showMfaFactorSelection && !showVerifyStep && (
          <>
            <SpinnerView isLoading={isLoginPending || isOtpRequestPending} />
            <ErrorProvider controller={errorController}>
              <LoginModeSwitch
                loginMode={loginMode}
                onSwitchMode={switchLoginMode}
                oneTimeCodeFormConfig={oneTimeCodeFormConfig}
                switchButtonClassName="log-in-redirection__authentication-btn"
                isOtpVerificationEnabled={isOtpVerificationEnabled}
                isLoginRedirectPage={true}
              >
                <section className="form__container">
                  <ErrorProvider controller={errorController}>
                    <LoginForm
                      setValidationError={errorController.setValidationError}
                      clearValidationError={
                        errorController.clearValidationError
                      }
                      clearErrors={errorController.clearErrors}
                      isLoading={isLoginPending}
                      onSubmit={handlePasswordSubmit}
                      prefilledEmail={prefilledEmailFromQuery}
                    />
                  </ErrorProvider>
                </section>
              </LoginModeSwitch>
            </ErrorProvider>
            <BackButton onClick={toBack} />
            {loginMode === LoginMode.PASSWORD && <ForgotPasswordLink />}
            <NeedHelpLink />
          </>
        )}
      </MfaErrorProvider>
    </div>
  );

  const renderAuthContent = () => {
    if (showMfaOrVerifyStep) {
      return renderMfaBlock();
    }
    if (idp === IDP_TYPE.GOOGLE || idp === IDP_TYPE.APPLE) {
      return renderIdpBlock(idp);
    }
    return renderEmailBlock();
  };

  return (
    <div className="log-in-redirection">
      {!showMfaOrVerifyStep && pageTitle}
      {renderAuthContent()}
    </div>
  );
};

export default LoginRedirect;
